/*
         This script exports a function that takes the application to be rendered
         to the DOM and packages and then renders it. The function ensures that the
         initial states of the applications have the same store created from the
         same reducer. It also ensures that the store register and BrowserRouter
         are given to every application.
 */

import React from "react"
import { createRoot } from "react-dom/client"
import { CookiesProvider } from "react-cookie"
import { Provider } from "react-redux"
import { BrowserRouter, Route } from "react-router-dom"
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk"
import * as Sentry from "@sentry/react"

import theme from "../../client/src/utils/theme"
import storeCreator from "../Utilities/store"
import connector from "../../client/src/utils/reduxRouter/connector"
import { isLocalhost } from "../../lib"

/**
 * Sentry.init() initializes the Sentry SDK with the DSN and other configuration options.
 */
if (!isLocalhost())
  Sentry.init({
    dsn: "https://812d2bc0086e4879b9e07f3cd604f028@o4508364190384128.ingest.de.sentry.io/4508364876873808",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      "localhost",
      `https://api.${window.location.host}`,
    ],
    // Session Replay
    replaysSessionSampleRate: 0, // This sets the sample rate at 0%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    // eslint-disable-next-line no-underscore-dangle
    environment: {
      prod: "production",
      test: "test",
      dev: "development",
      // eslint-disable-next-line no-undef
    }[__ENVIRONMENT_TYPE__],
  })

/*
     The register allows us to add reducers to the store.

     The reason we do not start with all reducers in the store is to keep
     the application lean and also not to include information on modules that
     the user may not have access to.

     Once a user accesses a route for a module component, the register will
     be used to load the reducer for the relevant component into the redux store

 */
const { store, register } = storeCreator()

const mapState = (state) => ({
  theme,
  register,
  store,
  loggedIn: state.sessionReducer.loggedIn,
  tcManager: state.sessionReducer.tcManager,
  render: state.sessionReducer.render,
  isSidebarOpen: state?.sidebarReducer?.isOpen || false,
})

const mapDispatch = () => ({})

const container = document.getElementById("app")

const root = createRoot(container)

export default async (App) => {
  const clientSideIDs = {
    prod: "66bdbab527e7230fd79b6c97",
    test: "66bdbab527e7230fd79b6c96",
    dev: "66bdd2bd0090e510646cb535",
  }
  const LDProvider = await asyncWithLDProvider({
    // eslint-disable-next-line no-underscore-dangle, no-undef
    clientSideID: clientSideIDs[__ENVIRONMENT_TYPE__],
    timeout: 3,
    context: {
      kind: "user",
      anonymous: true,
      key: "user-anonymous",
    },
  })

  root.render(
    <LDProvider>
      <CookiesProvider>
        <Provider store={store}>
          <BrowserRouter>
            <Route
              path="/"
              render={connector({
                component: App,
                mapState,
                mapDispatch,
              })}
            />
          </BrowserRouter>
        </Provider>
      </CookiesProvider>
    </LDProvider>
  )
}
